import React from 'react';
import { useTranslation } from 'react-i18next';
import { SelectValue } from 'antd/lib/select';
import { ColumnType } from 'antd/lib/table';
import { Row } from 'antd';

import {
  MCPServerAuthApiKeyStore,
  MCPServerAuthType,
  MCPServerHTTPHeader,
  MCPServerToolsetSettingsOutput,
  MCPServerTransport,
  ToolsetModel,
} from '../../../../../../gamma-api';
import IbInput, { IbInputType } from '../../../../../components/common/IbInput';
import IbSelect from '../../../../../components/common/IbSelect';
import { getEmptyAuthSettingsForType } from '../const';
import IbTypography from '../../../../../components/common/IbTypography';
import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';
import IbTable from '../../../../../components/common/IbTable';
import IbSwitch from '../../../../../components/common/IbSwitch';
import ConnectorBlock from '../../../../../components/ConnectorBlock';

import { FORM_ITEM_CLASS_NAME, PARAMETERS_CLASS_NAME, TABLE_CLEAR_CLASS_NAME, TABLE_REMOVE_CLASS_NAME } from './const';

export interface IMCPServerParametersProps {
  editToolset: ToolsetModel;
  onChange: (toolset: ToolsetModel) => void;
}

const MCPServerParameters: React.FC<IMCPServerParametersProps> = ({ editToolset, onChange }) => {
  const { t } = useTranslation();
  const settings = editToolset.settings as MCPServerToolsetSettingsOutput;

  const onNameChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        name: value,
      },
    } as ToolsetModel);
  };

  const onServerTypeChange = (value: SelectValue) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_transport: value as MCPServerTransport,
      },
    } as ToolsetModel);
  };

  const onServerAuthChange = (value: SelectValue) => {
    const authType = value as MCPServerAuthType;
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_type: authType,
          auth_settings: {
            ...(settings.server_auth.auth_settings ?? getEmptyAuthSettingsForType(authType)),
          },
        },
      },
    } as ToolsetModel);
  };

  const onServerAuthApiKeyProvideChange = (value: SelectValue) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            api_key_store: value as MCPServerAuthApiKeyStore,
          },
        },
      },
    } as ToolsetModel);
  };

  const onServerUrlChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_url: value,
      },
    } as ToolsetModel);
  };

  const onServerApiKeyChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            api_key_value: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onServerApiKeyNameChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            api_key_name: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onAuthorizeEndpointChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            authorize_endpoint: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onTokenEndpointChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            token_endpoint: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onClientIdChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            client_id: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onClientSecretChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            client_secret: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onScopeChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            scope: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onConnectorChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        server_auth: {
          ...settings.server_auth,
          auth_settings: {
            ...settings.server_auth.auth_settings,
            connector_id: value,
          },
        },
      },
    } as ToolsetModel);
  };

  const onHeaderAddClick = () => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        http_headers: [
          ...(settings.http_headers || []),
          {
            enabled: true,
            name: '',
            value: '',
          },
        ],
      },
    } as ToolsetModel);
  };

  const onHeaderEnabledChange = (index: number) => (enabled: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        http_headers: (settings.http_headers || []).map((header, idx) =>
          idx === index ? { ...header, enabled } : header
        ),
      },
    } as ToolsetModel);
  };

  const onHeaderNameChange = (index: number) => (name: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        http_headers: (settings.http_headers || []).map((header, idx) =>
          idx === index ? { ...header, name } : header
        ),
      },
    } as ToolsetModel);
  };

  const onHeaderValueChange = (index: number) => (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        http_headers: (settings.http_headers || []).map((header, idx) =>
          idx === index ? { ...header, value } : header
        ),
      },
    } as ToolsetModel);
  };

  const onHeadersClearClick = () => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        http_headers: [],
      },
    } as ToolsetModel);
  };

  const onHeaderDeleteClick = (index: number) => () => {
    onChange({
      ...editToolset,
      settings: {
        ...settings,
        http_headers: (settings.http_headers || []).filter((_, idx) => idx !== index),
      },
    } as ToolsetModel);
  };

  const headerColumns: ColumnType<MCPServerHTTPHeader>[] = [
    {
      title: t('HTTP: Headers: Name'),
      render: (_, record, index) => (
        <Row>
          <IbSwitch checked={record.enabled} size="small" onChange={onHeaderEnabledChange(index)} />
          <IbInput disabled={!record.enabled} value={record.name} onChange={onHeaderNameChange(index)} />
        </Row>
      ),
      width: '50%',
    },
    {
      title: t('HTTP: Headers: Value'),
      render: (_, record, index) => (
        <IbInput disabled={!record.enabled} value={record.value} onChange={onHeaderValueChange(index)} />
      ),
      width: '50%',
    },
    {
      title: (
        <div className={TABLE_CLEAR_CLASS_NAME}>
          <IbButton type="link" onClick={onHeadersClearClick}>
            {t('HTTP: Headers: Clear')}
          </IbButton>
        </div>
      ),
      render: (_, __, index) => (
        <IbButton icon={<IbIcon iconName="delete" />} type="icon" onClick={onHeaderDeleteClick(index)} />
      ),
      className: TABLE_REMOVE_CLASS_NAME,
    },
  ];

  return (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Toolset: Modal: Settings: Name: Title')}
        </IbTypography.Paragraph>
        <IbInput value={settings.name} onChange={onNameChange} />
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Toolset: Modal: Settings: MCP server type: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          options={[
            { value: MCPServerTransport.Sse, label: t('Agent: Toolset: Modal: SSE') },
            { value: MCPServerTransport.Streamable, label: t('Agent: Toolset: Modal: Streamable') },
          ]}
          value={settings.server_transport}
          onChange={onServerTypeChange}
        />
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Toolset: Modal: Settings: MCP server url: Title')}
        </IbTypography.Paragraph>
        <IbInput value={settings.server_url} onChange={onServerUrlChange} />
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Toolset: Modal: Settings: Auth Type: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          options={[
            { value: MCPServerAuthType.NoAuth, label: t('Agent: Toolset: Modal: No Auth') },
            { value: MCPServerAuthType.ApiKey, label: t('Agent: Toolset: Modal: API Key') },
            { value: MCPServerAuthType.OAuth2, label: t('Agent: Toolset: Modal: OAuth') },
            { value: MCPServerAuthType.Elma365, label: t('Agent: Toolset: Modal: Elma365') },
          ]}
          value={settings.server_auth.auth_type}
          onChange={onServerAuthChange}
        />
      </div>
      {settings.server_auth.auth_type === MCPServerAuthType.ApiKey && (
        <>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Toolset: Modal: Settings: API Key Name: Title')}
            </IbTypography.Paragraph>
            <IbInput value={settings.server_auth.auth_settings?.api_key_name} onChange={onServerApiKeyNameChange} />
          </div>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Toolset: Modal: Settings: API Key Value: Title')}
            </IbTypography.Paragraph>
            <IbInput
              type={IbInputType.Password}
              value={settings.server_auth.auth_settings?.api_key_value}
              onChange={onServerApiKeyChange}
            />
          </div>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Agent: Toolset: Modal: Settings: Add to: Title')}
            </IbTypography.Paragraph>
            <IbSelect
              options={[
                { value: MCPServerAuthApiKeyStore.Header, label: t('Agent: Toolset: Modal: Header') },
                { value: MCPServerAuthApiKeyStore.QueryParams, label: t('Agent: Toolset: Modal: Query Params') },
              ]}
              value={settings.server_auth.auth_settings?.api_key_store}
              onChange={onServerAuthApiKeyProvideChange}
            />
          </div>
        </>
      )}
      {settings.server_auth.auth_type === MCPServerAuthType.OAuth2 && (
        <>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('OAuth: Authorize Endpoint')}
            </IbTypography.Paragraph>
            <IbInput
              value={settings.server_auth.auth_settings?.authorize_endpoint}
              onChange={onAuthorizeEndpointChange}
            />
          </div>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('OAuth: Token Endpoint')}
            </IbTypography.Paragraph>
            <IbInput value={settings.server_auth.auth_settings?.token_endpoint} onChange={onTokenEndpointChange} />
          </div>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('OAuth: Client ID')}
            </IbTypography.Paragraph>
            <IbInput
              type={IbInputType.Password}
              value={settings.server_auth.auth_settings?.client_id}
              onChange={onClientIdChange}
            />
          </div>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('OAuth: Client Secret')}
            </IbTypography.Paragraph>
            <IbInput
              type={IbInputType.Password}
              value={settings.server_auth.auth_settings?.client_secret}
              onChange={onClientSecretChange}
            />
          </div>
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('OAuth: Scope')}
            </IbTypography.Paragraph>
            <IbInput value={settings.server_auth.auth_settings?.scope} onChange={onScopeChange} />
          </div>
        </>
      )}
      {settings.server_auth.auth_type === MCPServerAuthType.Elma365 && (
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Toolset: Modal: Elma365')}
          </IbTypography.Paragraph>
          <ConnectorBlock
            connectorId={settings.server_auth.auth_settings?.connector_id || ''}
            onChange={onConnectorChange}
          />
        </div>
      )}
      <div className={FORM_ITEM_CLASS_NAME}>
        <Row>
          <IbTypography.Paragraph strong type="secondary">
            {t('HTTP: Headers: List')}
          </IbTypography.Paragraph>
          <IbButton icon={<IbIcon iconName="add-one" />} type="link" onClick={onHeaderAddClick} />
        </Row>
        {settings.http_headers?.length ? (
          <IbTable columns={headerColumns} dataSource={settings.http_headers} size="small" />
        ) : null}
      </div>
    </div>
  );
};

export default MCPServerParameters;
